/*
 * Who can do what: one table for the whole app. Every server route names one action from ACTIONS and the guard asks can().
 * Anything not listed here is refused (default is deny).
 *
 * owner and franchise_owner have the same powers (except adding companies); what differs is where (a franchise owner only
 * ever holds a role in their own locations, and owner roles carry down to child companies, franchise owner roles do not).
 * See companies/tree.ts.
 */
import type { Role } from "./core.js";

export const ACTIONS = [
  // Companies and settings
  "company.read",
  "company.create",
  "company.update",
  "settings.read",
  "settings.update",
  "keys.read",
  "keys.update",
  "report.children",
  // People
  "people.read",
  "people.invite",
  "people.update",
  "people.delete",
  "people.update_roles",
  // Records kept by the app
  "audit.read",
  "outbox.read",
  // Work (later drafts build these routes)
  "client.read",
  "client.update",
  "client.merge",
  "client.bulk",
  /** See a client's hub (My Trades) the way the client sees it. Read only. Owners and managers, not schedulers. */
  "client.view_hub",
  "job.read",
  "job.update",
  "schedule.read",
  "schedule.update",
  "visit.read",
  "visit.update",
  // Money
  "invoice.read",
  "invoice.update",
  "invoice.send",
  "invoice.edit_paid",
  "payment.record",
  "card.charge",
  /** The dispute file (client page, More, Dispute file): every proof that a charge was right. Owners and managers. */
  "dispute.read",
  /** Send a dispute's proof to Stripe. Stripe takes it once, so owners only. */
  "dispute.send",
  // Sales
  "quote.read",
  "quote.update",
  "lead.read",
  "lead.update",
  "payroll.read",
  "payroll.update",
  "payouts.settings",
  "insights.read",
  "books.read",
  "books.update",
  // Head office and franchises (draft 11)
  /** The Head office page: companies below, territories, people across companies, franchise plans. Owners only. */
  "hq.read",
  "hq.update",
  /** The security center: logins, devices, access review, sign out, remove access, activity. */
  "security.read",
  "security.update",
  /** A franchise's own plan, bills and card for The Trades' fee. */
  "franchise.read",
  "franchise.update",
  // Marketing suite (draft 12)
  /** Google profile, ranks, ads and conversions, channels, social posts, cold email and the lead finder. */
  "marketing.read",
  "marketing.update",
  /** Turns cold email sending on or off. Owners only (owner rule: the owner switches sending on). */
  "outreach.send",
  // A crew member's own things
  "myday.read",
  /** A person's own visits on the phone: timer, checklist, photos, crew notes, Complete. Only visits they are on. */
  "myvisit.update",
  "mypay.read",
  "clock.update",
  /** A person's own profile picture and circle color. */
  "profile.update",
] as const;
export type Action = (typeof ACTIONS)[number];

const ALL = new Set<Action>(ACTIONS);

/**
 * Office managers: everything except company settings (company details and settings), keys, deleting people, payout
 * settings, reports across child companies, charging a saved card by hand (owners only, BUILD-SPEC 3.7), Books (owners
 * only, as in the WordPress Steward), and head office, security and the franchise plan (owners only, draft 11).
 */
const MANAGER = new Set<Action>(
  ACTIONS.filter(
    (a) =>
      ![
        "company.create",
        "company.update",
        "settings.update",
        "keys.read",
        "keys.update",
        "people.delete",
        "payouts.settings",
        "report.children",
        "card.charge",
        "dispute.send",
        "books.read",
        "books.update",
        "hq.read",
        "hq.update",
        "security.read",
        "security.update",
        "franchise.read",
        "franchise.update",
        "outreach.send",
      ].includes(a),
  ),
);

/**
 * Office schedulers: schedule, clients, jobs, visits, quotes, leads, checking and sending invoices. No payroll, no editing
 * paid invoices, no recording payments, no charging cards, and no merging clients or changing many at once (owner, franchise owner and manager only).
 */
const DISPATCH = new Set<Action>([
  "company.read",
  "settings.read",
  "people.read",
  "client.read",
  "client.update",
  "job.read",
  "job.update",
  "schedule.read",
  "schedule.update",
  "visit.read",
  "visit.update",
  "invoice.read",
  "invoice.update",
  "invoice.send",
  "quote.read",
  "quote.update",
  "lead.read",
  "lead.update",
  "myday.read",
  "myvisit.update",
  "mypay.read",
  "clock.update",
  "profile.update",
]);

/** Crew: their own day, their own visits, clocking in and out, their own pay. Prices are scrubbed from answers (see scrub). */
const CREW = new Set<Action>(["myday.read", "myvisit.update", "mypay.read", "clock.update", "profile.update"]);

/**
 * Franchise owners: owner powers inside their own location(s), but they do not add companies or run head office (HQ and
 * brand owners do).
 */
const FRANCHISE_OWNER = new Set<Action>(ACTIONS.filter((a) => !["company.create", "hq.read", "hq.update"].includes(a)));

const TABLE: Record<Role, ReadonlySet<Action>> = {
  owner: ALL,
  franchise_owner: FRANCHISE_OWNER,
  manager: MANAGER,
  dispatch: DISPATCH,
  crew: CREW,
};

/** May this role do this action? Unknown roles and actions get no. */
export function can(role: Role, action: Action): boolean {
  return TABLE[role].has(action);
}

/** How senior a role is, for "you can only give roles at or below your own". */
const RANK: Record<Role, number> = { crew: 1, dispatch: 2, manager: 3, franchise_owner: 4, owner: 5 };

/** May someone with this role invite or give that role? Owners give any role, everyone else only roles below their own. */
export function canGiveRole(giver: Role, role: Role): boolean {
  if (giver === "owner") return true;
  return RANK[role] < RANK[giver];
}

/**
 * Roles the Team page can move someone between. Owners and franchise owners are given from Head office only, so a typo on
 * the Team page can never hand out owner powers.
 */
export const TEAM_ROLES = ["crew", "dispatch", "manager"] as const satisfies readonly Role[];
export type TeamRole = (typeof TEAM_ROLES)[number];

/** May this role change the role of someone who is now `current`? Only people on a Team role below your own. */
export function canChangeRoleOf(giver: Role, current: string): boolean {
  return (TEAM_ROLES as readonly string[]).includes(current) && canGiveRole(giver, current as Role);
}

/** The Team roles this role may give. */
export function teamRolesFor(giver: Role): TeamRole[] {
  return TEAM_ROLES.filter((r) => canGiveRole(giver, r));
}

/** Roles that must use two-step sign in (an authenticator app code after the password). */
export const TWO_STEP_ROLES: readonly Role[] = ["owner", "franchise_owner", "manager"];
